/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import React, {createContext, useContext, useState, useCallback} from 'react';
import {USER_ROLES} from '#utils/constants';

interface ResetPasswordFlow {
  currentStep: number;
  isEmail: boolean;
  isCheckEmail: boolean;
  isReset: boolean;
  isComplete: boolean;
}

interface AuthState {
  currentRegistrationStep: number;
  resetPasswordFlow: ResetPasswordFlow;
  role: USER_ROLES | '';
  orgCode: string;
  userDetails: {[key: string]: any};
}

interface AuthContextType extends AuthState {
  setValuesByFieldName: (payload: Partial<AuthState>) => void;
  resetUserDetails: () => void;
  setUserDetails: (payload: {[key: string]: any}) => void;
  setResetPasswordFlow: (payload: Partial<ResetPasswordFlow>) => void;
  reset: () => void;
}

const initialState: AuthState = {
  currentRegistrationStep: 0,
  resetPasswordFlow: {
    currentStep: 0,
    isEmail: false,
    isCheckEmail: false,
    isReset: false,
    isComplete: false
  },
  role: '' as USER_ROLES,
  orgCode: '',
  userDetails: {}
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({children}: {children: React.ReactNode}) {
  const [state, setState] = useState<AuthState>(initialState);

  const setValuesByFieldName = useCallback((payload: Partial<AuthState>) => {
    setState(prev => ({
      ...prev,
      ...payload
    }));
  }, []);

  const resetUserDetails = useCallback(() => {
    setState(prev => ({
      ...prev,
      userDetails: {}
    }));
  }, []);

  const setUserDetails = useCallback((payload: {[key: string]: any}) => {
    setState(prev => ({
      ...prev,
      userDetails: {...prev.userDetails, ...payload}
    }));
  }, []);

  const setResetPasswordFlow = useCallback(
    (payload: Partial<ResetPasswordFlow>) => {
      setState(prev => ({
        ...prev,
        resetPasswordFlow: {...prev.resetPasswordFlow, ...payload}
      }));
    },
    []
  );

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        setValuesByFieldName,
        resetUserDetails,
        setUserDetails,
        setResetPasswordFlow,
        reset
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
